import React, { useEffect, useState } from 'react';
import { bool, func, shape } from 'prop-types';
import { useForm } from 'react-hook-form';
import { AUIModal } from '@ancert/aui-modal';
import { AUIIconCell } from '@ancert/aui-table-cell';
import AUIInputUploadBasic from '@ancert/aui-input-upload-basic';
import AUIInputTextAreaField from '@ancert/aui-input-textarea-field';
import { useNotification } from '@ancert/aui-notification-context';
import Field from '../Field';
import { defaultValues, resolver } from './form';
import { MAX_FILE_SIZE, MAX_TEXT_AREA } from '../../config';
import { getFileType, getFileIcon } from '../../utils/fileUtils';
import getDownload from '../../services/download.service';
import getBase64FromUrl from '../../utils/base64Utils';
import {
  downloadJpgBase64File,
  downloadMp3Base64File,
  downloadPdfBase64File,
  downloadPngBase64File,
} from '../../utils/downloadUtils';

const ACCEPT_UPLOAD_FILES = {
  description: 'pdf, png, jpg',
  filter: ['.pdf', '.png', '.jpg'],
};

const ModalArchivosAdjuntos = ({ editData, show, onClose, onAccept, downloadMode }) => {
  const { openNotification } = useNotification();
  const { control, formState, handleSubmit, reset } = useForm({
    defaultValues,
    resolver,
    mode: 'onSubmit',
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (editData) {
      reset(editData);
      setFile({ preloadFileName: editData.name });
    }
  }, [editData]);

  const onSubmit = (data) => {
    if (!file) {
      setFile({ ...file, error: true });
      return;
    }
    onAccept({ ...data, ...file });
    setFile(null);
    reset(defaultValues);
  };

  const closeModal = () => {
    reset(defaultValues);
    setFile(null);
    onClose();
  };

  const onSelectFile = (fileSelected) => {
    const newFile = {
      type: getFileType(fileSelected?.rawFile.name),
      name: fileSelected?.rawFile.name,
      file: fileSelected,
      preloadFileName: null,
    };
    setFile(newFile);
  };

  const download = async ({ blob, fileName }) => {
    const urlDownLoad = URL.createObjectURL(blob);
    const doc = await getBase64FromUrl(urlDownLoad);
    if (fileName.endsWith('.pdf')) {
      downloadPdfBase64File({ base64String: doc, fileName });
    } else if (fileName.endsWith('.png')) {
      downloadPngBase64File({ base64String: doc, fileName });
    } else if (fileName.endsWith('.jpg')) {
      downloadJpgBase64File({ base64String: doc, fileName });
    } else {
      downloadMp3Base64File({ base64String: doc, fileName });
    }
    closeModal();
  };

  const downloadFile = async () => {
    const { uuid, name } = editData;
    const { status, error, data: result } = await getDownload(uuid);
    if (status !== 200) {
      closeModal();
      const { message } = error;
      openNotification({
        title: 'Error',
        content: <div>{message || 'Ha ocurrido un error'}</div>,
        type: 'error',
        centerButtons: true,
      });
    } else {
      download({ blob: result, fileName: name });
    }
  };

  const buttons = [
    {
      id: 'primary',
      mode: 'primary',
      label: downloadMode ? 'Descargar' : 'Aceptar',
      onClick: downloadMode ? downloadFile : handleSubmit(onSubmit),
    },
    {
      id: 'tertiary',
      mode: 'tertiary',
      label: 'Cancelar',
      onClick: closeModal,
      disabled: false,
    },
  ];

  return (
    <AUIModal
      size="lg"
      title={downloadMode ? 'Descargar archivo de la comunicación' : 'Adjuntar archivo a la comunicación'}
      show={show}
      actionButtons={buttons}
      autoHeight
      onClose={closeModal}
    >
      <form autoComplete="off">
        <div className="o-grid">
          <div className="o-grid__row">
            <div className="o-grid__col-12">
              {!downloadMode ? (
                <AUIInputUploadBasic
                  required
                  preloadFileName={file?.preloadFileName}
                  name="documento"
                  label="Fichero"
                  maxFileSize={MAX_FILE_SIZE}
                  accept={ACCEPT_UPLOAD_FILES}
                  error={file?.error ? 'Es necesario adjuntar un archivo' : ''}
                  onRemoveFile={() => setFile(null)}
                  onSelectFile={onSelectFile}
                />
              ) : (
                <div>
                  <Field label="Fichero">
                    <AUIIconCell icon={getFileIcon(editData.name)} description={editData.name} />
                  </Field>
                </div>
              )}
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-12">
              {!downloadMode ? (
                <AUIInputTextAreaField
                  id="description"
                  name="description"
                  label="Descripción (máx. 150 caracteres)"
                  controlRef={control}
                  maxLength={MAX_TEXT_AREA}
                  errors={formState.errors}
                />
              ) : (
                <Field label="Descripción (máx. 150 caracteres)">{editData.description}</Field>
              )}
            </div>
          </div>
        </div>
      </form>
    </AUIModal>
  );
};
ModalArchivosAdjuntos.propTypes = {
  editData: shape({}),
  show: bool,
  onAccept: func,
  onClose: func,
  downloadMode: bool,
};
ModalArchivosAdjuntos.defaultProps = {
  editData: null,
  downloadMode: false,
  show: false,
  onAccept: null,
  onClose: null,
};

export default ModalArchivosAdjuntos;
